import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Avatar,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { CarnivoreStream } from '../_types/stream.type'

interface Props {
  stream: CarnivoreStream
}

const CarnivoreStreamComponent: React.FC<Props> = ({ stream }) => {
  // Sort messages & attendees by timestamp
  const messages = [...stream.messages].sort(
    (a, b) =>
      new Date(b.sentAt ?? '').getTime() -
      new Date(a.sentAt ?? '').getTime()
  )
  const attendees = [...stream.attendees].sort(
    (a, b) =>
      new Date(b.joinedAt ?? '').getTime() -
      new Date(a.joinedAt ?? '').getTime()
  )

  // Highlight state
  const [msgHighlight, setMsgHighlight] = useState(false)
  const [attHighlight, setAttHighlight] = useState(false)
  const prevMsgCount = useRef(messages.length)
  const prevAttCount = useRef(attendees.length)

  // Watch for new messages
  useEffect(() => {
    if (messages.length > prevMsgCount.current) {
      setMsgHighlight(true)
      setTimeout(() => setMsgHighlight(false), 1000)
    }
    prevMsgCount.current = messages.length
  }, [messages.length])

  // Watch for new attendees
  useEffect(() => {
    if (attendees.length > prevAttCount.current) {
      setAttHighlight(true)
      setTimeout(() => setAttHighlight(false), 1000)
    }
    prevAttCount.current = attendees.length
  }, [attendees.length])

  const accordionSx = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    mb: 2,
    '&:before': { display: 'none' },
  }

  const headerSx = (highlight: boolean) => ({
    bgcolor: highlight ? 'primary.light' : 'transparent',
    transition: 'background-color 0.3s ease',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  })

  return (
    <Box sx={{ width: '100%', maxHeight: '80vh', overflow: 'hidden' }}>
      {/* Messages Accordion */}
      <Accordion defaultExpanded sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={headerSx(msgHighlight)}>
            <Typography variant="h6">Messages ({messages.length})</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {messages.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography color="textSecondary">No messages yet.</Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: '40vh', overflowY: 'auto', p: 2 }}>
              {messages.map((msg, idx) => {
                const time = new Date(msg.sentAt ?? '').toLocaleTimeString(
                  'en-US',
                  { timeStyle: 'short' }
                )
                return (
                  <Box key={idx} display="flex" alignItems="flex-start" mb={2}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {msg.name
                        .split(' ')
                        .map(n => n[0])
                        .join('')
                        .toUpperCase()}
                    </Avatar>
                    <Box ml={1} maxWidth="75%">
                      <Box display="flex" alignItems="center">
                        <Box textAlign={'left'}>
                          <Typography variant="subtitle2">{msg.name}</Typography>
                          <Typography variant="body2">{msg.email}</Typography>
                        </Box>


                        <Typography
                          variant="caption"
                          color="textSecondary"
                          ml={1}
                        >
                          {time}
                        </Typography>
                      </Box>
                      <Box
                        mt={0.5}
                        p={1}
                        sx={{
                          bgcolor: 'grey.100',
                          borderRadius: 2,
                        }}
                      >
                        <Typography variant="body1" textAlign="left">
                          {msg.message}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )
              })}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Attendees Accordion */}
      <Accordion defaultExpanded sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={headerSx(attHighlight)}>
            <Typography variant="h6">Attendees ({attendees.length})</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {attendees.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography color="textSecondary">No attendees yet.</Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: '40vh', overflowY: 'auto', p: 2 }}>
              {attendees.map((att) => {
                const joined = new Date(att.joinedAt ?? '').toLocaleTimeString(
                  'en-US',
                  { timeStyle: 'short' }
                )
                return (
                  <Box
                    key={att.id}
                    display="flex"
                    mb={1}
                  >
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {att.name
                        .split(' ')
                        .map(n => n[0])
                        .join('')
                        .toUpperCase()}
                    </Avatar>
                    <Box ml={1} textAlign={'start'}>
                      <Typography variant="subtitle2">{att.name}</Typography>
                      <Typography variant="body2">{att.email}</Typography>
                      <Typography variant="body2">{att.phone}</Typography>

                      <Typography variant="caption" color="textSecondary">
                        Joined {joined}
                      </Typography>
                    </Box>
                  </Box>
                )
              })}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

export default CarnivoreStreamComponent
